import { supabase } from '../lib/supabase';

export interface WorkflowPhoto {
  id: string;
  booking_id: string;
  workflow_step_id: string;
  service_id: string;
  file_name: string;
  file_path: string;
  file_size?: number;
  mime_type?: string;
  uploaded_by?: string;
  created_at: string;
  updated_at: string;
}

export interface UploadPhotoParams {
  bookingId: string;
  workflowStepId: string;
  serviceId: string;
  file: File;
}

export interface UploadPhotoResult {
  success: boolean;
  photo?: WorkflowPhoto;
  error?: string;
}

/**
 * Upload a photo for a workflow step
 */
export async function uploadWorkflowPhoto({
  bookingId,
  workflowStepId,
  serviceId,
  file
}: UploadPhotoParams): Promise<UploadPhotoResult> {
  try {
    console.log('📸 Starting photo upload:', { bookingId, workflowStepId, serviceId, fileName: file.name });

    // Generate unique file name
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `workflow-photos/${bookingId}/${workflowStepId}/${fileName}`;

    console.log('📸 Generated file path:', filePath);

    // Upload file to Supabase storage
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('workflow-photos')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) {
      console.error('❌ Storage upload error:', uploadError);
      return { success: false, error: `Upload failed: ${uploadError.message}` };
    }

    console.log('✅ File uploaded to storage:', uploadData.path);

    // Save photo record to database
    console.log('📸 Inserting photo record:', { 
      bookingId, 
      workflowStepId, 
      serviceId: serviceId || 'null', 
      fileName: file.name 
    });

    const insertData: any = {
      booking_id: bookingId,
      workflow_step_id: workflowStepId,
      file_name: file.name,
      file_path: filePath,
      file_size: file.size,
      mime_type: file.type
    };

    // Only include service_id if it's a valid UUID
    if (serviceId && serviceId.length > 0 && serviceId !== '') {
      insertData.service_id = serviceId;
    }

    const { data: photoData, error: dbError } = await supabase
      .from('workflow_photos')
      .insert(insertData)
      .select()
      .single();

    if (dbError) {
      console.error('❌ Database insert error:', dbError);
      
      // Clean up uploaded file if database insert fails
      await supabase.storage
        .from('workflow-photos')
        .remove([filePath]);
      
      return { success: false, error: `Database error: ${dbError.message}` };
    }

    console.log('✅ Photo record saved to database:', photoData.id);

    return { success: true, photo: photoData };
  } catch (error) {
    console.error('❌ Unexpected error uploading photo:', error);
    return { success: false, error: 'Unexpected error occurred' };
  }
}

/**
 * Get photos for a specific workflow step
 */
export async function getWorkflowStepPhotos(
  bookingId: string,
  workflowStepId: string
): Promise<WorkflowPhoto[]> {
  try {
    console.log('📸 Fetching photos for step:', { bookingId, workflowStepId });

    const { data, error } = await supabase
      .from('workflow_photos')
      .select('*')
      .eq('booking_id', bookingId)
      .eq('workflow_step_id', workflowStepId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('❌ Error fetching workflow photos:', error);
      return [];
    }

    console.log('✅ Found photos:', data?.length || 0);
    return data || [];
  } catch (error) {
    console.error('❌ Unexpected error fetching photos:', error);
    return [];
  }
}

/**
 * Get all photos for a booking
 */
export async function getBookingWorkflowPhotos(bookingId: string): Promise<WorkflowPhoto[]> {
  try {
    const { data, error } = await supabase
      .from('workflow_photos')
      .select('*')
      .eq('booking_id', bookingId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('❌ Error fetching booking photos:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('❌ Unexpected error fetching booking photos:', error);
    return [];
  }
}

/**
 * Delete a workflow photo
 */
export async function deleteWorkflowPhoto(photoId: string): Promise<boolean> {
  try {
    console.log('📸 Deleting photo:', photoId);

    // First get the photo record to get the file path
    const { data: photo, error: fetchError } = await supabase
      .from('workflow_photos')
      .select('file_path')
      .eq('id', photoId)
      .single();

    if (fetchError || !photo) {
      console.error('❌ Error fetching photo for deletion:', fetchError);
      return false;
    }

    // Delete from storage
    const { error: storageError } = await supabase.storage
      .from('workflow-photos')
      .remove([photo.file_path]);

    if (storageError) {
      console.error('❌ Error deleting from storage:', storageError);
      // Continue with database deletion even if storage deletion fails
    }

    // Delete from database
    const { error: dbError } = await supabase
      .from('workflow_photos')
      .delete()
      .eq('id', photoId);

    if (dbError) {
      console.error('❌ Error deleting from database:', dbError);
      return false;
    }

    console.log('✅ Photo deleted successfully');
    return true;
  } catch (error) {
    console.error('❌ Unexpected error deleting photo:', error);
    return false;
  }
}

/**
 * Get public URL for a workflow photo
 */
export function getWorkflowPhotoUrl(filePath: string): string {
  const { data } = supabase.storage
    .from('workflow-photos')
    .getPublicUrl(filePath);
  
  return data.publicUrl;
}

/**
 * Convert blob URL to File object for upload
 */
export async function blobUrlToFile(blobUrl: string, fileName: string): Promise<File> {
  const response = await fetch(blobUrl);
  const blob = await response.blob();
  return new File([blob], fileName, { type: blob.type });
}
